import {
  Product,
  toProduct,
  toProductPostReqDTO,
  toProductPutReqDTO,
} from "../model/product";
import { ProductGetResDTO } from "./product-dto";
import { getApiClient } from "../../../shared/api/client.api";
import { CommonResponseDto } from "../../../shared/api/common-response.dto";

/**
 * Product API 클라이언트
 * Product 관련 API 호출을 담당
 */
export const manageProductApi = (() => {
  const apiClient = getApiClient();

  const getProductList = async (): Promise<Product[]> => {
    try {
      const response =
        await apiClient.get<CommonResponseDto<ProductGetResDTO[]>>("/products");

      if (!response.data.success || !response.data.data) {
        throw new Error("Product list data not found");
      }

      return response.data.data.map((product: ProductGetResDTO) =>
        toProduct(product),
      );
    } catch (error) {
      console.error("Failed to fetch product list:", error);
      throw error;
    }
  };

  const getProduct = async (productId: string): Promise<Product> => {
    try {
      const response = await apiClient.get<CommonResponseDto<ProductGetResDTO>>(
        `/products/${productId}`,
      );

      if (!response.data.success || !response.data.data) {
        throw new Error("Product data not found");
      }

      return toProduct(response.data.data);
    } catch (error) {
      console.error("Failed to fetch product:", error);
      throw error;
    }
  };

  const addProduct = async (
    product: Omit<Product, "id" | "createdAt" | "updatedAt">,
  ): Promise<ProductGetResDTO> => {
    try {
      const payload = toProductPostReqDTO(product);
      const response = await apiClient.post<
        CommonResponseDto<ProductGetResDTO>
      >("/products", payload);

      if (!response.data.success || !response.data.data) {
        throw new Error("Failed to add product");
      }

      return response.data.data;
    } catch (error) {
      console.error("Failed to add product:", error);
      throw error;
    }
  };

  const updateProduct = async (
    productId: string,
    product: Partial<Product>,
  ): Promise<ProductGetResDTO> => {
    try {
      const payload = toProductPutReqDTO({ ...product, id: productId });
      const response = await apiClient.put<CommonResponseDto<ProductGetResDTO>>(
        `/products/${productId}`,
        payload,
      );

      if (!response.data.success || !response.data.data) {
        throw new Error("Failed to update product");
      }

      return response.data.data;
    } catch (error) {
      console.error("Failed to update product:", error);
      throw error;
    }
  };

  const deleteProduct = async (productId: string): Promise<void> => {
    try {
      await apiClient.delete(`/products/${productId}`);
    } catch (error) {
      console.error("Failed to delete product:", error);
      throw error;
    }
  };

  return {
    getProductList,
    getProduct,
    addProduct,
    updateProduct,
    deleteProduct,
  };
})();
